// IconText.js
// -----------------------------------------------------------------------------
// Small horizontal row of an icon followed by text. Handy for feature lists,
// bullet points with icons, contact rows, etc.
//
// Props:
//   - icon     : node     (required)        leading icon element
//   - children : node     (required)        text content
//   - color    : 'primary'|'accent'|'neutral'|'inherit' (default 'primary')
//                color applied to the icon
//   - align    : 'start'|'center' (default 'center') vertical alignment
//   - level    : string   (default 'body-md') Typography level for the text
//   - sx       : object   (default {})       extra Joy sx overrides
// -----------------------------------------------------------------------------

import * as React from 'react';
import PropTypes from 'prop-types';
import Stack from '@mui/joy/Stack';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';

const ICON_COLORS = {
  primary: 'primary.500',
  accent: 'accent.600',
  neutral: 'neutral.500',
  inherit: 'inherit',
};

const IconText = ({
  icon,
  children,
  color = 'primary',
  align = 'center',
  level = 'body-md',
  sx = {},
}) => {
  // Zero-trust: guard required props and constrain enums.
  if (!icon || children === undefined || children === null) {
    return null;
  }
  const iconColor = ICON_COLORS[color] || ICON_COLORS.primary;
  const resolvedAlign = align === 'start' ? 'flex-start' : 'center';

  return (
    <Stack direction="row" spacing={1.5} sx={{ alignItems: resolvedAlign, ...sx }}>
      <Box
        sx={{
          color: iconColor,
          display: 'inline-flex',
          flexShrink: 0,
          mt: align === 'start' ? '2px' : 0,
        }}
      >
        {icon}
      </Box>
      <Typography level={level}>{children}</Typography>
    </Stack>
  );
};

IconText.propTypes = {
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['primary', 'accent', 'neutral', 'inherit']),
  align: PropTypes.oneOf(['start', 'center']),
  level: PropTypes.string,
  sx: PropTypes.object,
};

export default IconText;
